import { useState, useEffect, useRef } from 'react';
import { evaluatePromotions } from '../../services/promotionEngine';
import { promotionsService } from '../../services/api';

/**
 * Loads active promotions (backend when online, local SQLite when offline) and
 * evaluates them against the cart.
 * Re-evaluates whenever selectedProducts, selectedDiningOption, or subtotal changes.
 *
 * Override behaviour:
 *   userSelectedPromoIdRef.current === null    → auto mode: follow autoApplied
 *   userSelectedPromoIdRef.current === 'none'  → user explicitly removed; stays suppressed until
 *                                                 the cart is cleared or resetPromo() is called
 *   userSelectedPromoIdRef.current === <id>    → user picked a specific promo; kept unless it
 *                                                 becomes ineligible (then falls back to auto)
 *
 * Cart → engine mapping:
 *   basket item { product_code, category, quantity, selling_price }
 *   → engine line { productCode, category, quantity, unitPrice }
 * Main product lines only (addons are NOT separate cart lines; subtotal DOES include addon prices).
 */
const usePromoEvaluator = ({ selectedProducts, selectedDiningOption, subtotal, enabled = true }) => {
    const [promotions, setPromotions] = useState([]);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [autoAppliedOption, setAutoAppliedOption] = useState(null);
    const [manualOptions, setManualOptions] = useState([]);
    const [showPromoModal, setShowPromoModal] = useState(false);

    // Ref tracks the user's deliberate choice without causing re-evaluation loops
    const userSelectedPromoIdRef = useRef(null);

    // Load promotions from local DB once (or when enabled changes)
    useEffect(() => {
        if (!enabled) return;
        const load = async () => {
            try {
                // Online → backend (enabled only); offline/Electron → local DB. Web mode
                // (the primary mode) has no local DB, so this must not be local-only.
                const promos = await promotionsService.getActiveForPos();
                setPromotions(promos || []);
            } catch (e) {
                console.warn('[usePromoEvaluator] Failed to load promotions:', e?.message);
            }
        };
        load();
    }, [enabled]);

    // Re-evaluate on cart / dining-option / subtotal / promos change
    useEffect(() => {
        if (!enabled || !promotions.length) {
            setManualOptions([]);
            return;
        }

        if (!selectedProducts || !selectedProducts.length) {
            setManualOptions([]);
            setAutoAppliedOption(null);
            setAppliedPromo(null);
            userSelectedPromoIdRef.current = null;
            return;
        }

        const cart = selectedProducts.map(p => ({
            productCode: p.product_code,
            category: p.category,
            quantity: parseInt(p.quantity) || 1,
            unitPrice: parseFloat(p.selling_price) || 0,
        }));

        const { autoApplied, manualOptions: opts } = evaluatePromotions({
            cart,
            subtotal: subtotal || 0,
            diningOption: selectedDiningOption || '',
            now: new Date(),
            promotions,
        });

        setManualOptions(opts);
        setAutoAppliedOption(autoApplied);

        const usp = userSelectedPromoIdRef.current;
        if (usp === null) {
            // Auto mode: follow the best automatic promo
            setAppliedPromo(autoApplied);
        } else if (usp === 'none') {
            // User explicitly removed: keep null
            setAppliedPromo(null);
        } else {
            // User picked a specific promo — check it is still eligible
            const allEligible = [autoApplied, ...opts].filter(Boolean);
            const chosen = allEligible.find(e => e.promotion.promotion_id === usp);
            if (chosen) {
                setAppliedPromo(chosen);
            } else {
                // Lost eligibility: fall back to auto
                userSelectedPromoIdRef.current = null;
                setAppliedPromo(autoApplied);
            }
        }
    }, [selectedProducts, selectedDiningOption, subtotal, promotions, enabled]);

    const selectPromo = (option) => {
        userSelectedPromoIdRef.current = option.promotion.promotion_id;
        setAppliedPromo(option);
        setShowPromoModal(false);
    };

    const removePromo = () => {
        userSelectedPromoIdRef.current = 'none';
        setAppliedPromo(null);
    };

    const resetPromo = () => {
        userSelectedPromoIdRef.current = null;
        setAppliedPromo(null);
        setManualOptions([]);
    };

    // All currently-eligible promos: auto first, then manual (deduped by promotion_id)
    const autoId = autoAppliedOption?.promotion?.promotion_id;
    const allEligibleOptions = autoAppliedOption
        ? [autoAppliedOption, ...manualOptions.filter(o => o.promotion.promotion_id !== autoId)]
        : manualOptions;

    return {
        appliedPromo,
        manualOptions,
        allEligibleOptions,
        showPromoModal,
        setShowPromoModal,
        selectPromo,
        removePromo,
        resetPromo,
        promoDiscountAmount: appliedPromo ? appliedPromo.discount : 0,
    };
};

export default usePromoEvaluator;
